import type { Goal, GoalOutcomeStatus } from '../../types/api';
import {
  GOAL_METRIC_CONFIG,
  type GoalChartKey,
  type GoalMetricDirection,
} from './goalConfig';

export interface GoalProjectionPoint {
  date: string;
  value: number;
}

export interface GoalActualPoint {
  date: string;
  value: number | null | undefined;
}

export interface GoalChartRange {
  startMonth: string;
  endMonth: string;
}

export interface GoalGap {
  native: number;
  targetRelativePercentage: number | null;
}

export interface GoalMilestone {
  goalId: number;
  label: string;
  shortLabel: string;
  color: string;
  deadlineMonth: string;
  targetValue: number;
  actualValue: number | null;
  gap: GoalGap | null;
  outcome: GoalOutcomeStatus;
  sequenceStatus: 'progressive' | 'legacy_invalid';
  lifecycleStatus: Goal['status'];
  progressStatus: Goal['progress_status'];
}

export interface GoalBenchmarkSegment {
  goalId: number;
  shortLabel: string;
  color: string;
  startMonth: string;
  endMonth: string;
  targetValue: number;
  points: GoalProjectionPoint[];
}

export interface GoalOriginalTrajectory {
  goalId: number;
  shortLabel: string;
  color: string;
  startMonth: string;
  deadlineMonth: string;
  baselineValue: number;
  targetValue: number;
  points: GoalProjectionPoint[];
}

export interface GoalChartPlan {
  chartKey: GoalChartKey;
  label: string;
  unit: string;
  direction: GoalMetricDirection;
  actualAnchor: GoalProjectionPoint | null;
  requiredPace: GoalProjectionPoint[];
  originalTrajectories: GoalOriginalTrajectory[];
  milestones: GoalMilestone[];
  benchmarks: GoalBenchmarkSegment[];
}

const GOAL_SERIES_COLORS = [
  'var(--brand-600)',
  'var(--info-600)',
  'var(--warning-600)',
  'var(--accent-teal-600)',
  'var(--neutral-700)',
  'var(--success-700)',
] as const;

interface BuildGoalChartPlanOptions {
  goals: readonly Goal[];
  chartKey: GoalChartKey;
  actuals: readonly GoalActualPoint[];
  visibleRange: GoalChartRange;
  now?: Date;
}

export function toMonth(value: string): string {
  return value.slice(0, 7);
}

function parseMonth(
  value: string,
): { year: number; monthIndex: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (!match?.[1] || !match[2]) return null;
  return {
    year: Number(match[1]),
    monthIndex: Number(match[2]) - 1,
  };
}

function monthDifference(startMonth: string, endMonth: string): number {
  const start = parseMonth(startMonth);
  const end = parseMonth(endMonth);
  if (!start || !end) return -1;
  return (end.year - start.year) * 12 + end.monthIndex - start.monthIndex;
}

export function addMonths(monthValue: string, amount: number): string {
  const parsed = parseMonth(monthValue);
  if (!parsed) return monthValue;
  const date = new Date(Date.UTC(parsed.year, parsed.monthIndex + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function getLatestCompletedMonth(now = new Date()): string {
  // Match Analytics filter date helpers, which use the local calendar month.
  const currentMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1,
  ).padStart(2, '0')}`;
  return addMonths(currentMonth, -1);
}

export function getMonthRange(startMonth: string, endMonth: string): string[] {
  const months = monthDifference(startMonth, endMonth);
  if (months < 0) return [];
  return Array.from({ length: months + 1 }, (_, index) =>
    addMonths(startMonth, index),
  );
}

export function getGoalDisplayRange(
  selectedRange: GoalChartRange,
  goals: readonly Goal[],
  now = new Date(),
): GoalChartRange {
  const currentMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1,
  ).padStart(2, '0')}`;
  const furthestActiveGoalMonth = goals
    .filter(
      (goal) =>
        goal.status === 'active' && toMonth(goal.deadline_date) >= currentMonth,
    )
    .reduce(
      (furthest, goal) =>
        toMonth(goal.deadline_date) > furthest
          ? toMonth(goal.deadline_date)
          : furthest,
      selectedRange.endMonth,
    );

  return {
    startMonth: selectedRange.startMonth,
    endMonth:
      furthestActiveGoalMonth > selectedRange.endMonth
        ? furthestActiveGoalMonth
        : selectedRange.endMonth,
  };
}

export function clipActualsToCompletedMonths(
  actuals: readonly GoalActualPoint[],
  now = new Date(),
): GoalActualPoint[] {
  const latestCompletedMonth = getLatestCompletedMonth(now);
  return actuals.filter(
    (actual) => toMonth(actual.date) <= latestCompletedMonth,
  );
}

function normalizeGoalValue(goal: Goal, value: number): number {
  return value * GOAL_METRIC_CONFIG[goal.metric].displayMultiplier;
}

function isProgressiveTarget(
  direction: GoalMetricDirection,
  previousTarget: number,
  target: number,
): boolean {
  return direction === 'lower_is_better'
    ? target <= previousTarget
    : target >= previousTarget;
}

export function calculateGoalGap(
  direction: GoalMetricDirection,
  actualValue: number,
  targetValue: number,
): GoalGap {
  const native =
    direction === 'lower_is_better'
      ? actualValue - targetValue
      : targetValue - actualValue;
  return {
    native,
    targetRelativePercentage:
      targetValue === 0 ? null : (native / Math.abs(targetValue)) * 100,
  };
}

function deriveOutcome(
  goal: Goal,
  direction: GoalMetricDirection,
  targetValue: number,
  deadlineActualValue: number | null,
  latestCompletedMonth: string,
): GoalOutcomeStatus {
  if (goal.outcome_status) return goal.outcome_status;
  if (toMonth(goal.deadline_date) > latestCompletedMonth) return 'upcoming';
  if (deadlineActualValue === null) return 'unavailable';
  const achieved =
    direction === 'lower_is_better'
      ? deadlineActualValue <= targetValue
      : deadlineActualValue >= targetValue;
  return achieved ? 'achieved' : 'missed';
}

function interpolateSegment(
  start: GoalProjectionPoint,
  end: GoalProjectionPoint,
): GoalProjectionPoint[] {
  const months = monthDifference(start.date, end.date);
  if (months <= 0) return [end];
  return Array.from({ length: months + 1 }, (_, index) => ({
    date: addMonths(start.date, index),
    value: start.value + ((end.value - start.value) * index) / months,
  }));
}

function clipPoints(
  points: readonly GoalProjectionPoint[],
  range: GoalChartRange,
): GoalProjectionPoint[] {
  return points.filter(
    (point) => point.date >= range.startMonth && point.date <= range.endMonth,
  );
}

export function getGoalSeriesColor(index: number): string {
  return (
    GOAL_SERIES_COLORS[index % GOAL_SERIES_COLORS.length] ?? 'var(--brand-600)'
  );
}

export function formatGoalShortLabel(deadlineMonth: string): string {
  const formatted = new Intl.DateTimeFormat('es-ES', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${deadlineMonth}-01T00:00:00Z`));
  return `Meta ${formatted.replace(/\./g, '')}`;
}

function emptyPlan(
  chartKey: GoalChartKey,
  label: string,
  unit: string,
  direction: GoalMetricDirection,
): GoalChartPlan {
  return {
    chartKey,
    label,
    unit,
    direction,
    actualAnchor: null,
    requiredPace: [],
    originalTrajectories: [],
    milestones: [],
    benchmarks: [],
  };
}

export function buildGoalChartPlan({
  goals,
  chartKey,
  actuals,
  visibleRange,
  now = new Date(),
}: BuildGoalChartPlanOptions): GoalChartPlan {
  const latestCompletedMonth = getLatestCompletedMonth(now);
  const matchingGoals = goals
    .filter(
      (goal) =>
        goal.status !== 'paused' &&
        GOAL_METRIC_CONFIG[goal.metric].chartKey === chartKey &&
        typeof goal.target_value === 'number' &&
        Number.isFinite(goal.target_value),
    )
    .sort(
      (left, right) =>
        toMonth(left.deadline_date).localeCompare(
          toMonth(right.deadline_date),
        ) || left.id - right.id,
    );
  const firstGoal = matchingGoals[0];
  const fallbackConfig = Object.values(GOAL_METRIC_CONFIG).find(
    (config) => config.chartKey === chartKey,
  );
  const config = firstGoal
    ? GOAL_METRIC_CONFIG[firstGoal.metric]
    : fallbackConfig;
  if (!config) {
    return emptyPlan(chartKey, chartKey, '', 'lower_is_better');
  }

  const validActuals = clipActualsToCompletedMonths(actuals, now)
    .filter(
      (
        actual,
      ): actual is GoalActualPoint & {
        value: number;
      } =>
        typeof actual.value === 'number' &&
        Number.isFinite(actual.value) &&
        toMonth(actual.date) <= visibleRange.endMonth,
    )
    .sort((left, right) =>
      toMonth(left.date).localeCompare(toMonth(right.date)),
    );
  const latestActual = validActuals[validActuals.length - 1];
  const actualAnchor = latestActual
    ? { date: toMonth(latestActual.date), value: latestActual.value }
    : null;

  let previousTarget: number | null = null;
  const allMilestones = matchingGoals.map((goal, index): GoalMilestone => {
    const targetValue = normalizeGoalValue(goal, goal.target_value ?? 0);
    const sequenceStatus =
      previousTarget === null ||
      isProgressiveTarget(config.direction, previousTarget, targetValue)
        ? 'progressive'
        : 'legacy_invalid';
    previousTarget = targetValue;
    const rawDeadlineActual =
      typeof goal.deadline_month_actual === 'number' &&
      Number.isFinite(goal.deadline_month_actual)
        ? normalizeGoalValue(goal, goal.deadline_month_actual)
        : null;
    const deadlineMonth = toMonth(goal.deadline_date);
    const comparisonActual =
      deadlineMonth <= latestCompletedMonth
        ? rawDeadlineActual
        : (actualAnchor?.value ?? null);
    return {
      goalId: goal.id,
      label: config.label,
      shortLabel: formatGoalShortLabel(deadlineMonth),
      color: getGoalSeriesColor(index),
      deadlineMonth,
      targetValue,
      actualValue: comparisonActual,
      gap:
        comparisonActual === null
          ? null
          : calculateGoalGap(config.direction, comparisonActual, targetValue),
      outcome: deriveOutcome(
        goal,
        config.direction,
        targetValue,
        rawDeadlineActual,
        latestCompletedMonth,
      ),
      sequenceStatus,
      lifecycleStatus: goal.status,
      progressStatus: goal.progress_status,
    };
  });

  const originalTrajectories = matchingGoals.flatMap(
    (goal, index): GoalOriginalTrajectory[] => {
      if (
        typeof goal.baseline_value !== 'number' ||
        !Number.isFinite(goal.baseline_value)
      ) {
        return [];
      }
      const startMonth = toMonth(goal.day_zero_date ?? goal.created_at);
      const deadlineMonth = toMonth(goal.deadline_date);
      if (monthDifference(startMonth, deadlineMonth) < 0) return [];
      const baselineValue = normalizeGoalValue(goal, goal.baseline_value);
      const targetValue = normalizeGoalValue(goal, goal.target_value ?? 0);
      const points = clipPoints(
        interpolateSegment(
          { date: startMonth, value: baselineValue },
          { date: deadlineMonth, value: targetValue },
        ),
        visibleRange,
      );
      if (points.length === 0) return [];
      return [
        {
          goalId: goal.id,
          shortLabel: formatGoalShortLabel(deadlineMonth),
          color: getGoalSeriesColor(index),
          startMonth,
          deadlineMonth,
          baselineValue,
          targetValue,
          points,
        },
      ];
    },
  );

  const futureProgressiveMilestones = allMilestones.filter(
    (milestone) =>
      milestone.sequenceStatus === 'progressive' &&
      actualAnchor !== null &&
      milestone.deadlineMonth > actualAnchor.date &&
      milestone.deadlineMonth <= visibleRange.endMonth,
  );
  const requiredPace = actualAnchor
    ? futureProgressiveMilestones.reduce<GoalProjectionPoint[]>(
        (points, milestone) => {
          const start = points[points.length - 1] ?? actualAnchor;
          const segment = interpolateSegment(start, {
            date: milestone.deadlineMonth,
            value: milestone.targetValue,
          });
          return [...points, ...segment.slice(points.length > 0 ? 1 : 0)];
        },
        [],
      )
    : [];

  const benchmarks = allMilestones.flatMap(
    (milestone, index): GoalBenchmarkSegment[] => {
      const nextMilestone = allMilestones[index + 1];
      const unclippedEnd = nextMilestone
        ? addMonths(nextMilestone.deadlineMonth, -1)
        : visibleRange.endMonth;
      const startMonth =
        milestone.deadlineMonth < visibleRange.startMonth
          ? visibleRange.startMonth
          : milestone.deadlineMonth;
      const endMonth =
        unclippedEnd > visibleRange.endMonth
          ? visibleRange.endMonth
          : unclippedEnd;
      if (endMonth < startMonth) return [];
      return [
        {
          goalId: milestone.goalId,
          shortLabel: milestone.shortLabel,
          color: milestone.color,
          startMonth,
          endMonth,
          targetValue: milestone.targetValue,
          points: getMonthRange(startMonth, endMonth).map((date) => ({
            date,
            value: milestone.targetValue,
          })),
        },
      ];
    },
  );

  return {
    chartKey,
    label: config.label,
    unit: config.unit,
    direction: config.direction,
    actualAnchor,
    requiredPace: clipPoints(requiredPace, visibleRange),
    originalTrajectories,
    milestones: allMilestones.filter(
      (milestone) =>
        milestone.deadlineMonth >= visibleRange.startMonth &&
        milestone.deadlineMonth <= visibleRange.endMonth,
    ),
    benchmarks,
  };
}

export function getGoalOutcomeColor(outcome: GoalOutcomeStatus): string {
  if (outcome === 'achieved') return 'var(--success-600)';
  if (outcome === 'missed') return 'var(--danger-600)';
  if (outcome === 'unavailable') return 'var(--neutral-500)';
  return 'var(--brand-600)';
}
